/**
 * Map export — captures the WebGL canvas AND any HTML overlays the user
 * sees on top of it (legend, pin/text annotations, popups, status chips).
 *
 * MapLibre's WebGL pixels live on a `<canvas>`; HTML markers and the
 * legend live as DOM siblings. Calling `canvas.toDataURL()` only grabs
 * the canvas layer. To include overlays we render the entire `.map-wrap`
 * element through html2canvas, which composites both layers into one PNG.
 */
import html2canvas from 'html2canvas';
import { toast } from '../ui/toast.js';

export async function exportMap(map) {
  const wrap = document.querySelector('.map-wrap');
  if (!wrap) {
    toast('Nothing to export — map not found.');
    return;
  }

  // Force a fresh paint so the WebGL canvas reflects the latest tiles.
  // preserveDrawingBuffer is set on the map (see map/index.js) so the
  // canvas keeps its pixels for capture.
  map.triggerRepaint();
  await new Promise(r => map.once('render', r));
  // One animation frame for HTML-side updates (legend, annotations) to settle
  await new Promise(r => requestAnimationFrame(r));

  toast('Composing PNG…');
  try {
    const canvas = await html2canvas(wrap, {
      backgroundColor: null,
      useCORS: true,
      allowTaint: true,
      logging: false,
      // Skip elements the user wouldn't want in the export (control
      // buttons, hamburgers, the loading pill). Keep the legend, status
      // chips, basemap pill, time bar, and pin/text annotations.
      ignoreElements: (el) => {
        return el.classList?.contains('map-tools')
            || el.classList?.contains('map-loading')
            || el.classList?.contains('maplibregl-ctrl-attrib');
      },
    });

    const dataUrl = canvas.toDataURL('image/png');
    if (!dataUrl || dataUrl.length < 200) {
      toast('Export failed — try zooming or panning, then export again.');
      return;
    }
    const link = document.createElement('a');
    link.download = `geodaverse-${new Date().toISOString().slice(0, 10)}.png`;
    link.href = dataUrl;
    link.click();
    toast('Map exported as PNG');
  } catch (e) {
    console.error('Export error', e);
    toast(`Export failed: ${e.message}`);
  }
}
